import { el } from "../dom.ts";
import { type StatusTone, statusPresentation, type TransferStatusName } from "../format/status.ts";
import { type IconName, icon } from "./icon.ts";

/**
 * Status badges, notices, and the loading / empty / error states.
 *
 * Colour never carries meaning on its own anywhere in this file — every tone is
 * paired with an icon and a text label, per the UI UX Pro Max
 * `color-not-only` and `color-not-decorative-only` rules. That matters more here
 * than in most products: a person misreading a transfer's state is a financial
 * misunderstanding, not a cosmetic one.
 */

/** Badge for a transfer status from the frozen §12 set. */
export function statusBadge(status: TransferStatusName): HTMLElement {
  const presentation = statusPresentation(status);
  return el("span", { class: "nura-badge", data: { tone: presentation.tone } }, [
    el("span", { class: "nura-icon-slot", staticHtml: icon(presentation.icon, 14) }),
    el("span", { text: presentation.label }),
  ]);
}

export type NoticeTone = "info" | "success" | "attention" | "danger";

export interface NoticeOptions {
  readonly tone: NoticeTone;
  readonly title?: string;
  readonly text: string;
  /**
   * Announce the notice when it appears. Use for content that arrives in
   * response to something the person just did; omit for content present on
   * load, which a screen reader reads in document order anyway.
   */
  readonly live?: boolean;
  readonly icon?: IconName;
}

const NOTICE_ICONS: Readonly<Record<NoticeTone, IconName>> = {
  info: "info",
  success: "check-circle",
  attention: "warning",
  danger: "x-circle",
};

export function notice(options: NoticeOptions): HTMLElement {
  const glyph = options.icon ?? NOTICE_ICONS[options.tone];
  return el(
    "div",
    {
      class: "nura-notice",
      data: { tone: options.tone },
      attrs: options.live === true ? { role: "status", "aria-live": "polite" } : {},
    },
    [
      el("span", { class: "nura-notice__icon", staticHtml: icon(glyph, 20) }),
      el("div", { class: "nura-notice__body" }, [
        options.title === undefined
          ? undefined
          : el("p", { class: "nura-notice__title", text: options.title }),
        el("p", { class: "nura-notice__text", text: options.text }),
      ]),
    ],
  );
}

export interface StateOptions {
  readonly title: string;
  readonly text: string;
  readonly icon?: IconName;
  readonly tone?: Extract<StatusTone, "neutral" | "danger">;
  readonly action?: Node;
}

/** Empty and error states share a shape; only tone, icon and copy differ. */
export function state(options: StateOptions): HTMLElement {
  return el("div", { class: "nura-state", data: { tone: options.tone ?? "neutral" } }, [
    el("span", { class: "nura-state__icon", staticHtml: icon(options.icon ?? "tray", 22) }),
    el("h3", { class: "nura-state__title", text: options.title }),
    el("p", { class: "nura-state__text", text: options.text }),
    options.action,
  ]);
}

export function emptyState(options: Omit<StateOptions, "tone">): HTMLElement {
  return state({ ...options, tone: "neutral" });
}

export function errorState(options: Omit<StateOptions, "tone" | "icon">): HTMLElement {
  return state({ ...options, tone: "danger", icon: "warning" });
}

/**
 * Skeleton placeholder.
 *
 * Reserves the final layout so nothing shifts when content arrives, which is
 * the point — a spinner that collapses to a different height is a layout shift
 * dressed as feedback. `aria-hidden` keeps the placeholder out of the
 * accessibility tree; the region that owns it announces its own busy state.
 */
export function skeleton(width: string, height: string): HTMLElement {
  const node = el("span", { class: "nura-skeleton", attrs: { "aria-hidden": true } });
  node.style.display = "block";
  node.style.inlineSize = width;
  node.style.blockSize = height;
  return node;
}

/** A loading region: skeleton lines plus a single polite announcement. */
export function loadingBlock(label: string, lines = 3): HTMLElement {
  const widths = ["100%", "82%", "64%"];
  return el(
    "div",
    {
      class: "nura-stack",
      attrs: { "aria-busy": "true", role: "status", "aria-label": label },
    },
    Array.from({ length: lines }, (_unused, index) =>
      skeleton(widths[index % widths.length] ?? "100%", "var(--nura-skeleton-line)"),
    ),
  );
}
